// Internal-admin "Onboard an existing user" — bring a user that ALREADY has an API key
// (e.g. a legacy client) into a Console account WITHOUT changing their api_key. Search by
// username / email / api key, pick the row, attach a login email + role + (optional) API
// access. It creates an invite bound to that user id; on accept, that same row gets the
// email/username/password + this account/role, and its api_key is preserved.
import { useEffect, useState } from "react";
import {
  App,
  Modal,
  Input,
  List,
  Radio,
  Form,
  Select,
  Segmented,
  Tag,
  Typography,
  Space,
  Button,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { usersApi, invitesAdminApi } from "../../console/consoleApi";
import {
  errorMessage,
  invitableRolesFor,
  rolesForAccountType,
  ROLE_LABELS,
} from "../../console/consoleUtils";
import { useSession } from "../../console/useSession";

const { Text } = Typography;

export default function OnboardExistingUserModal({
  open,
  onClose,
  onDone,
  accountId,
  accountType = "external",
}) {
  const { message } = App.useApp();
  const { user: actor } = useSession();
  const [form] = Form.useForm();

  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [apiMode, setApiMode] = useState("all");
  const [apiOptions, setApiOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    usersApi
      .apiCollections()
      .then((res) =>
        setApiOptions((res.data.data.apiCollections || []).map((n) => ({ value: n, label: n })))
      )
      .catch(() => {});
  }, [open]);

  const roleOptions = invitableRolesFor(actor?.role)
    .filter((r) => rolesForAccountType(accountType).includes(r))
    .map((r) => ({ value: r, label: ROLE_LABELS[r] || r }));

  const reset = () => {
    setQ("");
    setResults([]);
    setSelected(null);
    setApiMode("all");
    form.resetFields();
  };
  const close = () => {
    reset();
    onClose();
  };

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const res = await usersApi.searchExisting(q.trim());
      setResults(res.data.data.users);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setSearching(false);
    }
  };

  const pick = (u) => {
    setSelected(u);
    form.setFieldsValue({ email: u.email || "" });
  };

  const submit = async (values) => {
    if (!selected) {
      message.error("Pick a user to onboard.");
      return;
    }
    const apiCollections = apiMode === "specific" ? values.apiCollections || [] : [];
    setSubmitting(true);
    try {
      await invitesAdminApi.create({
        email: values.email.trim(),
        role: values.role,
        accountId,
        targetUserId: selected.id,
        ...(apiCollections.length ? { apiCollections } : {}),
      });
      message.success(`Onboarding invite sent to ${values.email.trim()}.`);
      reset();
      onDone?.();
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Onboard an existing user"
      open={open}
      onCancel={close}
      onOk={() => form.submit()}
      okText="Send onboarding invite"
      okButtonProps={{ disabled: !selected }}
      confirmLoading={submitting}
      destroyOnHidden
      width={640}
    >
      <Text type="secondary">
        Search a user that already has an API key (e.g. a legacy client). Onboarding attaches a
        Console login (email + password) and this account/role to their existing row — their{" "}
        <b>API key does not change</b>, and "All APIs" leaves their current access untouched.
      </Text>

      <Space.Compact style={{ width: "100%", margin: "12px 0" }}>
        <Input
          placeholder="Search by username, email, or API key"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onPressEnter={search}
          prefix={<SearchOutlined />}
          allowClear
        />
        <Button type="primary" onClick={search} loading={searching}>
          Search
        </Button>
      </Space.Compact>

      {results.length > 0 && (
        <Radio.Group
          value={selected?.id}
          onChange={(e) => pick(results.find((u) => u.id === e.target.value))}
          style={{ width: "100%" }}
        >
          <List
            size="small"
            bordered
            dataSource={results}
            style={{ maxHeight: 220, overflow: "auto", marginBottom: 12 }}
            renderItem={(u) => (
              <List.Item>
                <Radio value={u.id}>
                  <Space wrap>
                    <b>{u.username}</b>
                    <Text type="secondary">{u.email || "no email"}</Text>
                    <Tag>key ••••{u.apiKeyLast4 || "----"}</Tag>
                    <Tag color={u.accountId ? "orange" : "default"}>
                      {u.accountId ? `account ${u.accountId}` : "no account"}
                    </Tag>
                    <Tag>{u.status}</Tag>
                  </Space>
                </Radio>
              </List.Item>
            )}
          />
        </Radio.Group>
      )}

      {selected && (
        <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
          <Form.Item
            name="email"
            label="Login email (attached to this user)"
            rules={[{ required: true, type: "email", message: "Enter a valid email" }]}
          >
            <Input placeholder="client@company.com" autoComplete="off" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true, message: "Pick a role" }]}>
            <Select placeholder="Select a role" options={roleOptions} />
          </Form.Item>
          <Form.Item label="API access" style={{ marginBottom: 8 }}>
            <Segmented
              value={apiMode}
              onChange={setApiMode}
              options={[
                { label: "All APIs (keep current)", value: "all" },
                { label: "Add specific APIs", value: "specific" },
              ]}
            />
          </Form.Item>
          {apiMode === "specific" && (
            <Form.Item
              name="apiCollections"
              label="APIs to add"
              rules={[{ required: true, message: "Pick at least one API, or choose All APIs" }]}
              help="These are ADDED to whatever access the user already has."
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="Select APIs"
                options={apiOptions}
                maxTagCount="responsive"
              />
            </Form.Item>
          )}
        </Form>
      )}
    </Modal>
  );
}
